import { inject, Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { CarriersInfo, OperatedByLabel, UnavailableIcon } from '@views/common/models';
import { Station } from '@vueling-ng/vy-cdk-searcher';
import { FlightDetailsData } from '@vueling-ng/vy-ds/src/lib/components/globals/flight-details';
import { FlightJourney as FlightJourneyDs } from '@vueling-ng/vy-ds/src/lib/components/globals/flight-journey';
import { FlightJourney } from '../models/booking-details.model';

@Injectable({ providedIn: 'root' })
export class DsComponentsMapper {
  private readonly _translateService: TranslateService = inject(TranslateService);

  public mapFlightJourneyData(flightJourney: FlightJourney | undefined, stations: Station[]): FlightJourneyDs {
    if (!flightJourney) {
      return null as unknown as FlightJourneyDs;
    }
    const nightFlightLabel = flightJourney.isNightFlight
      ? 'booking_details_component.flight_journey_summary_component.night_flight'
      : undefined;
    const earlyMorningFlightLabel = flightJourney.isEarlyMorning
      ? 'booking_details_component.early_morning_flight'
      : undefined;
    const plusDayLabel = flightJourney.isPlusDay
      ? 'booking_details_component.flight_journey_summary_component.plus_one_day'
      : undefined;
    const firstSegment = flightJourney.segments[0];
    const lastSegment = flightJourney.segments[flightJourney.segments.length - 1];
    const connectionTime = flightJourney.connectionTime ? this.mapTime(flightJourney.connectionTime) : undefined;
    const result = {
      placeOrigin: this.findStation(firstSegment.iataOrigin, stations) ?? firstSegment.iataOrigin,
      placeDestination: this.findStation(lastSegment?.iataDestination, stations) ?? lastSegment?.iataDestination,
      iataOrigin: firstSegment.iataOrigin,
      iataDestination: lastSegment.iataDestination,
      terminalLabel: 'booking_details_component.flight_journey_summary_component.terminal',
      terminalOrigin: firstSegment.terminalOrigin ?? '1',
      terminalDestination: lastSegment.terminalDestination ?? '1',
      airportOrigin: firstSegment.iataOrigin,
      airportDestination: lastSegment.iataDestination,
      dateOrigin: firstSegment.departureDate,
      dateDestination: lastSegment.arrivalDate,
      tripTime: flightJourney.totalTripTime,
      connectionTime: connectionTime,
      plusDay: plusDayLabel,
      flightDetails: this.mapFlightDetails(flightJourney),
      flightJourneyLabels: {
        connectionLabel: connectionTime
          ? 'booking_details_component.flight_journey_summary_component.connection'
          : 'booking_details_component.flight_journey_summary_component.one_way',
        plusDayLabel: plusDayLabel,
        nightFlightLabel: nightFlightLabel,
        earlyMorningFlightLabel: earlyMorningFlightLabel,
        time: connectionTime,
        station: firstSegment.iataDestination
      }
    };
    return result;
  }

  public mapTime(time: string): string {
    const [h, m] = time.split(':').map(Number);

    const parts: string[] = [];
    if (h > 0) parts.push(`${h}h`);
    if (m > 0) parts.push(`${m}min`);

    return parts.join(' ');
  }

  public mapFlightDetails(flightJourney: FlightJourney | undefined): FlightDetailsData[] {
    if (!flightJourney) {
      return [];
    }
    return flightJourney.segments.flatMap((segment) => {
      const carrierInfo = CarriersInfo[segment.codeShareOperatorName ?? segment.carrierName] ?? {
        operatorName: segment.carrierName
      };
      const flightNumber = segment.codeShareOperatorName
        ? `${segment.codeShareCarrierName} ${segment.codeShareFlightNumber}`
        : `${segment.carrierName} ${segment.flightNumber}`;
      return {
        operatorIcon: carrierInfo.operatorIcon ?? UnavailableIcon,
        operatorName:
          carrierInfo.operatorName === CarriersInfo['VY'].operatorName
            ? undefined
            : (this._translateService.instant(OperatedByLabel, {
                operator: carrierInfo.operatorName
              }) as string),
        flightNumber: flightNumber
      };
    });
  }

  public findStation(iata: string, stations: Station[]): string {
    return stations.find((station) => station.stationCode === iata)?.fullName ?? iata;
  }
}
